import { useRef, useEffect, useMemo } from "react";
import {
  getIdTokenClaims,
  trackHeapEvent,
} from "@hoteltradergit/ht-telemetry-ui-lib";

/**
 * Fires a card impression once the card is at least half visible.
 *
 * `alsoEmitAs` names a second event to send from the same impression, with the
 * same properties. It exists for cards whose spec names a dedicated event (e.g.
 * expiring_promo_card_viewed) that still need to appear in the shared
 * "Recommendation Card Viewed" series the Action Center's card-position
 * analytics are built on. Both names come off one observer, so a card cannot
 * report one and miss the other.
 */
const useHeapCardViewed = (
  cardType,
  cardPosition,
  hotelName,
  extraProps = {},
  alsoEmitAs = null
) => {
  const cardRef = useRef(null);
  const hasFiredRef = useRef(false);

  // Serialize extra props so the memo is stable across renders that pass an
  // equivalent object literal (e.g. BnBerry's { card_state }). Sort the keys so
  // the memo key is insensitive to property order for dynamically-built props.
  const extraPropsKey = JSON.stringify(
    extraProps,
    Object.keys(extraProps).sort((a, b) => a.localeCompare(b))
  );

  const properties = useMemo(
    () => ({
      card_type: cardType,
      card_position: cardPosition,
      page_section: "action_center",
      hotel_name: hotelName || null,
      user_type: getIdTokenClaims()?.["custom:userType"] || null,
      ...extraProps,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardType, cardPosition, hotelName, extraPropsKey]
  );

  useEffect(() => {
    const node = cardRef.current;
    if (
      !node ||
      hasFiredRef.current ||
      typeof IntersectionObserver === "undefined"
    )
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasFiredRef.current) {
            // trackHeapEvent reports whether the call dispatched; only
            // mark fired (and stop observing) once it actually did
            if (trackHeapEvent("Recommendation Card Viewed", properties)) {
              if (alsoEmitAs) {
                trackHeapEvent(alsoEmitAs, properties);
              }
              hasFiredRef.current = true;
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [properties, alsoEmitAs]);

  return cardRef;
};

export default useHeapCardViewed;
